import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2, Check, X, ShieldCheck, Lock,
  Calendar, Globe, Users,
} from "lucide-react";
import { api } from "../lib/api";
import { useUser } from "../contexts/UserContext";
import CategoryBadge from "../components/CategoryBadge";
import EmptyState from "../components/EmptyState";
import { Skeleton } from "../components/Skeleton";
import { timeAgo } from "../lib/date";

const EMPTY_ILLUSTRATION =
  "https://static.prod-images.emergentagent.com/jobs/1708729f-9648-4829-9f8b-880cdb94ab73/images/dbc2ef0c9db61f1146fb8d05447a662edab3d51a4c2a41690bac30f7aab9358b.png";

const SCOPE_LABEL = {
  global: "Monde entier", country: "Pays", region: "Région",
  group: "Groupe", level: "Niveau", direction: "Direction", custom: "Personnalisé",
};

export default function Moderation() {
  const { user } = useUser();
  const [tab, setTab] = useState("posts"); // posts | comments
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pendingComments, setPendingComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [rejectComment, setRejectComment] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [pendingRes, catsRes, commentsRes] = await Promise.all([
        api.get("/moderation/pending"),
        api.get("/categories"),
        api.get("/posts/moderation/comments/pending").catch(() => ({ data: [] })),
      ]);
      setItems(pendingRes.data);
      setCategories(catsRes.data);
      setPendingComments(commentsRes.data || []);
    } catch (e) {
      toast.error("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const catById = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.category_id, c])),
    [categories]
  );

  const decideComment = async (postId, commentId, decision) => {
    setActionLoading(commentId);
    try {
      await api.post(`/posts/${postId}/comments/${commentId}/decide`, { decision });
      toast.success(decision === "approved" ? "Commentaire publié." : "Commentaire refusé.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec.");
    } finally {
      setActionLoading(null);
    }
  };

  const lockItem = async (approvalId) => {
    setActionLoading(approvalId);
    try {
      await api.post("/moderation/lock", { approval_id: approvalId });
      toast.success("Verrouillé pour vous. Les autres membres ne peuvent plus agir.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verrouillage refusé.");
    } finally {
      setActionLoading(null);
    }
  };

  const decide = async (id, decision, comment) => {
    setActionLoading(id);
    try {
      await api.post(`/moderation/decide/${id}`, { decision, comment });
      toast.success(
        decision === "approved" ? "Post approuvé et publié." : "Post refusé."
      );
      setOpenId(null);
      setRejectComment("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec.");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div data-testid="moderation-page">
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheck size={28} color="#F77F00" />
        <div>
          <h1
            className="text-[28px] sm:text-[32px] font-bold leading-tight"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Modération
          </h1>
          <p className="text-sm" style={{ color: "#666" }}>
            {items.length} post{items.length > 1 ? "s" : ""} en attente
          </p>
        </div>
      </div>

      {loading && (
        <div>
          {[0, 1].map((i) => (
            <div
              key={i}
              className="bg-white rounded-[12px] p-6 mb-4"
              style={{ border: "1px solid #E5E5E5" }}
            >
              <Skeleton className="w-24 h-6 mb-3" />
              <Skeleton className="w-3/4 h-6 mb-2" />
              <Skeleton className="w-1/2 h-4 mb-4" />
              <Skeleton className="w-full h-12" />
            </div>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && tab === "posts" && (
        <EmptyState
          illustration={EMPTY_ILLUSTRATION}
          title="Boîte de modération vide"
          description="Tous les posts soumis ont été traités. Bon travail."
          testid="moderation-empty"
        />
      )}

      {!loading && tab === "comments" && pendingComments.length === 0 && (
        <EmptyState
          illustration={EMPTY_ILLUSTRATION}
          title="Aucun commentaire à modérer"
          description="Les commentaires en attente apparaîtront ici."
          testid="moderation-comments-empty"
        />
      )}

      {/* Comments tab */}
      {!loading && tab === "comments" && pendingComments.length > 0 && (
        <AnimatePresence mode="popLayout">
          {pendingComments.map((c) => (
            <motion.div
              key={c.comment_id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-[12px] p-5 mb-3"
              style={{ border: "1px solid #E5E5E5" }}
              data-testid={`pending-comment-${c.comment_id}`}
            >
              <div className="flex items-center gap-2 text-xs mb-2 flex-wrap" style={{ color: "#666" }}>
                <strong style={{ color: "#1A1A1A" }}>
                  {c.author?.name || "Membre"}
                </strong>
                <span>·</span>
                <span>{timeAgo(c.created_at)}</span>
                <span>·</span>
                <span>sur "{c.post?.title || "…"}"</span>
              </div>
              <p className="text-sm mb-3 p-3 rounded-lg" style={{ background: "#FAFAFA" }}>
                {c.content}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => decideComment(c.post_id, c.comment_id, "approved")}
                  disabled={actionLoading === c.comment_id}
                  data-testid={`approve-comment-${c.comment_id}`}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                  style={{ background: "#10B981" }}
                >
                  <Check size={14} className="inline -mt-0.5 mr-1" /> Publier
                </button>
                <button
                  onClick={() => decideComment(c.post_id, c.comment_id, "rejected")}
                  disabled={actionLoading === c.comment_id}
                  data-testid={`reject-comment-${c.comment_id}`}
                  className="px-4 py-1.5 rounded-full text-sm font-semibold border disabled:opacity-50 hover:bg-red-50"
                  style={{ borderColor: "#EF4444", color: "#EF4444" }}
                >
                  <X size={14} className="inline -mt-0.5 mr-1" /> Refuser
                </button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      )}

      <AnimatePresence mode="popLayout">
        {tab === "posts" && items.map((it) => {
          const isPool = !!it.moderator_pool;
          const isLocked = !!it.locked_by;
          const lockedByMe = it.locked_by === user?.user_key;
          const lockedByOther = isLocked && !lockedByMe;
          const cat = catById[it.post.category_id];
          return (
            <motion.div
              key={it.approval_id}
              layout
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-[12px] p-6 sm:p-7 mb-4"
              style={{
                border: "1px solid #E5E5E5",
                borderLeft: isPool ? "3px solid #F77F00" : "1px solid #E5E5E5",
              }}
              data-testid={`moderation-item-${it.approval_id}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <CategoryBadge category={cat} />
                  {isPool && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1"
                      style={{ background: "#FFF7ED", color: "#9A3412" }}
                      data-testid={`badge-pool-${it.approval_id}`}
                    >
                      Collège Direction
                    </span>
                  )}
                  {lockedByMe && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1"
                      style={{ background: "#D1FAE5", color: "#065F46" }}
                    >
                      <Lock size={10} /> Verrouillé par vous
                    </span>
                  )}
                  {lockedByOther && (
                    <span
                      className="px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider inline-flex items-center gap-1"
                      style={{ background: "#FEE2E2", color: "#991B1B" }}
                    >
                      <Lock size={10} /> Verrouillé
                    </span>
                  )}
                </div>
                <span className="text-xs" style={{ color: "#999" }}>
                  Soumis {timeAgo(it.created_at)}
                </span>
              </div>

              <h2
                className="font-bold text-lg sm:text-xl mb-2"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                {it.post.title}
              </h2>

              {/* Author + scope */}
              <div className="flex items-center gap-3 text-sm mb-4 flex-wrap" style={{ color: "#666" }}>
                <span className="inline-flex items-center gap-1.5">
                  {it.post.author?.avatar_url ? (
                    <img
                      src={it.post.author.avatar_url}
                      alt=""
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                      style={{ background: "#F77F00" }}
                    >
                      {it.post.author?.name?.split(" ").map((n) => n[0]).join("").slice(0, 2) || "?"}
                    </div>
                  )}
                  <strong style={{ color: "#1A1A1A" }}>{it.post.author?.name || "Auteur"}</strong>
                </span>
                <span className="inline-flex items-center gap-1">
                  <Globe size={12} /> {SCOPE_LABEL[it.post.target_scope] || it.post.target_scope}
                </span>
                {it.post.event_date && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar size={12} /> {new Date(it.post.event_date).toLocaleDateString("fr-FR")}
                  </span>
                )}
                {it.escalation_level > 0 && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
                    style={{ background: "#FEF3F2", color: "#991B1B" }}
                  >
                    Escaladé
                  </span>
                )}
              </div>

              <p
                className="text-sm leading-relaxed whitespace-pre-wrap line-clamp-5 mb-4 p-3 rounded-lg"
                style={{ background: "#FAFAFA" }}
              >
                {it.post.content}
              </p>

              <div className="flex flex-wrap gap-2 items-center">
                {isPool && !lockedByMe && (
                  <button
                    onClick={() => lockItem(it.approval_id)}
                    disabled={actionLoading === it.approval_id || lockedByOther}
                    data-testid={`lock-${it.approval_id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border disabled:opacity-50 transition-colors hover:bg-gray-50"
                    style={{ borderColor: "#E5E5E5", color: "#1A1A1A" }}
                  >
                    <Lock size={14} /> Verrouiller pour moi
                  </button>
                )}

                <button
                  onClick={() => decide(it.approval_id, "approved")}
                  disabled={actionLoading === it.approval_id || lockedByOther}
                  data-testid={`approve-${it.approval_id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                  style={{ background: "#10B981" }}
                >
                  <Check size={14} /> Approuver
                </button>

                <button
                  onClick={() => setOpenId(openId === it.approval_id ? null : it.approval_id)}
                  data-testid={`reject-toggle-${it.approval_id}`}
                  disabled={lockedByOther}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border disabled:opacity-50 transition-colors hover:bg-red-50"
                  style={{ borderColor: "#EF4444", color: "#EF4444" }}
                >
                  <X size={14} /> Refuser
                </button>
              </div>

              {openId === it.approval_id && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="mt-3 pt-3 border-t overflow-hidden"
                  style={{ borderColor: "#E5E5E5" }}
                >
                  <label className="text-xs font-medium mb-1.5 block" style={{ color: "#666" }}>
                    Motif du refus (visible par l'auteur, 10 caractères minimum)
                  </label>
                  <textarea
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    placeholder="Ex : Contenu hors-sujet, non aligné sur les valeurs APOLLO…"
                    rows={2}
                    data-testid={`reject-comment-${it.approval_id}`}
                    className="w-full p-3 rounded-lg border text-sm outline-none focus:border-orange-500"
                    style={{ borderColor: "#E5E5E5" }}
                  />
                  <button
                    onClick={() => decide(it.approval_id, "rejected", rejectComment)}
                    disabled={rejectComment.trim().length < 10 || actionLoading === it.approval_id}
                    data-testid={`reject-confirm-${it.approval_id}`}
                    className="mt-2 px-4 py-2 rounded-full text-sm font-semibold text-white disabled:opacity-50 transition-opacity hover:opacity-90"
                    style={{ background: "#EF4444" }}
                  >
                    Confirmer le refus
                  </button>
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
